/**
 * API Service for GenAI Chat Application
 * Handles all communication with the backend API
 * Includes authentication, error handling, and request/response processing
 */

import axios from 'axios';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { handleApiError, handleNetworkError } from './errorHandler';
import { trackApiCall, trackError } from './monitoringService';
import { getApiConfig, getChatConfig, getWebSocketConfig } from '../config/config';
import { generateMilitaryTitle } from '../utils/sessionUtils';

// Phase 999.107 commit 7/7 — consent_required (HTTP 403) interceptor helper.
// Shared by every axios instance in this file. Server-side gate
// (session_manager_v5, chat_handler_v9) returns 403 {error:'consent_required'}
// when the user hasn't accepted the current Privacy Policy version.
//
// Behavior:
//   - Not on /onboarding -> redirect there so user can re-accept.
//   - Already on /onboarding -> stay put (the page itself drives consent
//     collection). Either way, HALT the promise chain so the caller (e.g.
//     App.js createChatSession bootstrap) does not see the error and retry,
//     which would cause /sessions to fire repeatedly while consent is
//     unresolved (loop symptom reported 999.107-hotfix).
//   - Call was to /user -> let it through; user-service-v3 IS the
//     consent-WRITE path. Redirecting on its 403 would deadlock first-time
//     acceptance.
//
// Returns true if the consent_required signal was handled (caller MUST
// return a never-resolving promise to halt the in-flight request chain).
const handleConsentRequired = (error) => {
  const is403Consent =
    error?.response?.status === 403 &&
    error?.response?.data?.error === 'consent_required';
  if (!is403Consent) return false;
  if (error?.config?.url?.includes('/user')) return false;

  const current = error.response.data.consent_version;
  const accepted = error.response.data.accepted_version;
  const onOnboarding = window.location.pathname.startsWith('/onboarding');

  if (onOnboarding) {
    console.warn(
      `[consent] 403 on /onboarding (already there) — suppressing error to break App.js retry loop; current=${current} accepted=${accepted ?? 'null'}`
    );
  } else {
    console.warn(
      `[consent] 403 consent_required current=${current} accepted=${accepted ?? 'null'} - redirecting to /onboarding`
    );
    window.location.assign('/onboarding');
  }
  return true;
};

// SOLUTION 3: Message Validation Layer
// ====================================
// Validates user input before sending to backend

/**
 * Validates a chat message before sending to the backend
 * @param {string} message - The user message to validate
 * @param {string} sessionId - The session ID to validate
 * @returns {Object} - Validation result with isValid and errors
 */
const validateChatMessage = (message, sessionId) => {
    const errors = [];
    const warnings = [];
    
    // Message validation
    if (!message || typeof message !== 'string') {
        errors.push('Message is required and must be a string');
    } else {
        // Trim whitespace for validation
        const trimmedMessage = message.trim();
        
        if (trimmedMessage.length === 0) {
            errors.push('Message cannot be empty or only whitespace');
        } else if (trimmedMessage.length < 2) {
            errors.push('Message must be at least 2 characters long');
        } else if (trimmedMessage.length > 4000) {
            errors.push('Message is too long (maximum 4000 characters)');
        }
        
        // Content validation
        if (trimmedMessage.includes('<script>') || trimmedMessage.includes('</script>')) {
            errors.push('Message contains potentially harmful content');
        }

        // Injection pattern check (defence-in-depth — backend also filters)
        const injectionPattern = /(\[SYSTEM\]|\[INSTRUCTION\]|##\s|--\s*ignore|ignore\s+above|<prompt>|<system>|\bprompt injection\b)/i;
        if (injectionPattern.test(trimmedMessage)) {
            errors.push('Message contains invalid content');
        }
        
        // Warning for very long messages
        if (trimmedMessage.length > 2000) {
            warnings.push('Very long message - consider breaking into smaller parts');
        }
    }
    
    // Session ID validation
    if (!sessionId || typeof sessionId !== 'string') {
        errors.push('Session ID is required');
    } else if (sessionId.trim().length === 0) {
        errors.push('Session ID cannot be empty');
    } else if (sessionId.length > 100) {
        errors.push('Session ID is too long');
    }
    
    // Rate limiting check (basic client-side)
    const now = Date.now();
    const lastMessageTime = window.lastMessageTime || 0;
    const timeSinceLastMessage = now - lastMessageTime;
    
    if (timeSinceLastMessage < 1000) { // Less than 1 second
        warnings.push('Please wait a moment between messages');
    }
    
    // Update timestamp
    window.lastMessageTime = now;
    
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        sanitizedMessage: message ? message.trim() : '',
        metadata: {
            messageLength: message ? message.trim().length : 0,
            hasWarnings: warnings.length > 0,
            timeSinceLastMessage
        }
    };
};

/**
 * Enhanced error handling with user-friendly messages
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 * @returns {Object} - Formatted error response
 */
const handleChatError = (error, context = 'chat') => {
    console.error(`🚨 ${context} error:`, error);
    
    let userMessage = 'Something went wrong. Please try again.';
    let canRetry = true;
    let severity = 'error';
    
    const errorString = error.toString().toLowerCase();
    
    if (errorString.includes('network') || errorString.includes('fetch')) {
        userMessage = 'Network connection issue. Please check your internet and try again.';
        severity = 'warning';
    } else if (errorString.includes('timeout')) {
        userMessage = 'Request timed out. Please try again.';
        severity = 'warning';
    } else if (errorString.includes('unauthorized') || errorString.includes('authentication')) {
        userMessage = 'Please sign in again to continue.';
        canRetry = false;
        severity = 'error';
    } else if (errorString.includes('validation')) {
        userMessage = 'Please check your message and try again.';
        severity = 'warning';
    } else if (errorString.includes('rate limit') || errorString.includes('too many')) {
        userMessage = 'Please wait a moment before sending another message.';
        severity = 'warning';
    } else if (errorString.includes('service unavailable')) {
        userMessage = 'AI service is temporarily unavailable. Please try again in a few minutes.';
        severity = 'warning';
    }
    
    return {
        error: userMessage,
        originalError: error.message,
        canRetry,
        severity,
        timestamp: new Date().toISOString(),
        context
    };
  };

// Initialize configuration
const initializeApiConfig = () => {
  return {
    api: getApiConfig(),
    chat: getChatConfig()
  };
};

// Validation layer for chat messages
const validateMessage = (message) => {
  if (!message || typeof message !== 'string') {
    throw new Error('Invalid message format');
  }
  
  const config = initializeApiConfig();
  const minLength = config?.chat?.minMessageLength || 2;
  const maxLength = config?.chat?.maxMessageLength || 4000;
  
  if (message.length < minLength) {
    throw new Error(`Message must be at least ${minLength} characters long`);
  }
  
  if (message.length > maxLength) {
    throw new Error(`Message must not exceed ${maxLength} characters`);
  }
  
  return true;
};

const validateSessionId = (sessionId) => {
  if (!sessionId || typeof sessionId !== 'string') {
    throw new Error('Invalid session ID format');
  }
  
  const config = initializeApiConfig();
  const maxLength = config?.chat?.maxSessionIdLength || 100;
  
  if (sessionId.length > maxLength) {
    throw new Error(`Session ID must not exceed ${maxLength} characters`);
  }
  
  return true;
};

// Rate limiting
let lastRequestTime = 0;
const getRateLimitDelay = () => {
  const config = initializeApiConfig();
  return config?.chat?.rateLimitDelay || 1000;
};

const checkRateLimit = () => {
  const now = Date.now();
  const rateLimitDelay = getRateLimitDelay();
  if (now - lastRequestTime < rateLimitDelay) {
    throw new Error('Please wait before sending another message');
  }
  lastRequestTime = now;
};

// Error handling - removed duplicate function, using the one in errorHandler.js instead

// Initialize axios instance with configuration
const createAxiosInstance = async () => {
  const apiConfig = await initializeApiConfig();
  
  const instance = axios.create({
    baseURL: apiConfig.api.endpoint,
    timeout: apiConfig.api.timeout || 30000,
    headers: {
      'Content-Type': 'application/json',
      'X-Bedrock-Agent-Id': apiConfig.bedrock.agentId,
      'X-Bedrock-Agent-Alias': apiConfig.bedrock.agentAlias,
      'X-Bedrock-Model-Id': apiConfig.bedrock.modelId,
      'X-Bedrock-Max-Tokens': apiConfig.bedrock.maxTokens,
      'X-Bedrock-Temperature': apiConfig.bedrock.temperature
    }
  });

  // Request interceptor for authentication
  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = await getCurrentUser();
        if (user) {
          const session = await fetchAuthSession();
          if (session?.tokens?.accessToken) {
            config.headers.Authorization = `Bearer ${session.tokens.accessToken.toString()}`;
          }
        }
        return config;
      } catch (error) {
        console.error('Error in request interceptor:', error);
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Phase 999.107 commit 7 + hotfix: redirect to /onboarding on
      // consent_required when not already there. ALWAYS still reject so
      // callers' try/catch fires and any `finally { setAppReady(true) }`
      // bootstrap logic runs — a never-resolving promise would hang the
      // app on its preloader.
      handleConsentRequired(error);
      const errorMessage = handleApiError(error, 'API');
      console.error('API Error:', errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
  );

  return instance;
};

// Create axios instance
let api = null;
const getApi = async () => {
  if (!api) {
    api = await createAxiosInstance();
  }
  return api;
};

// API Configuration
const API_BASE_URL = 'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout to prevent hanging
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get current auth session
      const session = await fetchAuthSession();
      // Use ID token for API Gateway Cognito User Pool authorizer
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add request ID for tracking
      config.headers['X-Request-ID'] = generateRequestId();
      
      // Development mode - add origin header for CORS debugging
      if (isDevelopment) {
        config.headers['X-Development-Origin'] = window.location.origin;
      }
      
      // F-21: request logging is dev-only — production consoles must stay
      // clean of route patterns (veterans open DevTools; reviewers noticed).
      if (isDevelopment) {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url} [LOCALHOST]`);
      }
      return config;
    } catch (error) {
      console.error('❌ Error adding auth token:', error);
      return config;
    }
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for enhanced error handling with token refresh
apiClient.interceptors.response.use(
  (response) => {
    // F-21: dev-only (see request interceptor note)
    if (isDevelopment) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('❌ API Error:', error.response?.status, error.response?.data || error.message);

    // Phase 999.107 commit 7 + hotfix: trigger redirect on consent_required
    // (when not already on /onboarding) but let the rejection bubble — App.js
    // bootstrap relies on catch + finally to set appReady. A never-resolving
    // promise hangs the loader. Skip the 401 retry path since this is a 403.
    handleConsentRequired(error);
    if (error.response?.status === 403 && error.response?.data?.error === 'consent_required') {
      return Promise.reject(error);
    }

    // Handle specific error cases with enhanced logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 Token expired, attempting refresh...');
      originalRequest._retry = true;
      
      try {
        // Wait a bit before retrying to avoid race conditions
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Try to refresh the session with retry
        let session;
        let refreshAttempts = 0;
        const maxRefreshAttempts = 3;
        
        while (refreshAttempts < maxRefreshAttempts) {
          try {
            session = await fetchAuthSession({ forceRefresh: true });
            break;
          } catch (refreshError) {
            refreshAttempts++;
            if (refreshAttempts >= maxRefreshAttempts) {
              throw refreshError;
            }
            console.log(`🔄 Refresh attempt ${refreshAttempts} failed, retrying...`);
            // eslint-disable-next-line no-loop-func
            await new Promise(resolve => setTimeout(resolve, 1000 * refreshAttempts));
          }
        }
        
        const newToken = session?.tokens?.idToken?.toString();
        
        if (newToken) {
          console.log('✅ Token refreshed successfully');
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Reset retry flag for this specific request
          delete originalRequest._retry;
          
          return apiClient(originalRequest);
        } else {
          console.error('❌ Failed to get new token after refresh');
          throw new Error('No access token in refreshed session');
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        console.error('🔒 Authentication error - user needs to re-authenticate');
        
        // Clear any stored auth state
        try {
          window.localStorage.removeItem('amplify-signin-with-hostedUI');
          window.sessionStorage.clear();
        } catch (e) {
          console.warn('Could not clear storage:', e);
        }
        
        throw new Error('Authentication session expired. Please log in again.');
      }
    } else if (error.response?.status === 429) {
      console.error('⏱️ Rate limit exceeded - backing off');
      // Add exponential backoff for rate limits
      const retryAfter = error.response.headers['retry-after'] || Math.pow(2, originalRequest._retryCount || 0);
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
    } else if (error.response?.status >= 500) {
      console.error('🚨 Server error detected');
      
      // For 5xx errors, check if we should implement circuit breaker
      if (!window.serverErrorCount) window.serverErrorCount = 0;
      window.serverErrorCount++;
      
      if (window.serverErrorCount > 10) {
        console.error('🔴 Too many server errors - implementing circuit breaker');
        window.circuitBreakerActive = true;
        setTimeout(() => {
          window.circuitBreakerActive = false;
          window.serverErrorCount = 0;
          console.log('🟢 Circuit breaker reset');
        }, 30000); // 30 second circuit breaker
      }
    } else if (!error.response) {
      console.error('🌐 Network error or CORS issue detected');
    }
    
    return Promise.reject(error);
  }
);

// Generate unique request ID
const generateRequestId = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// API Service Class
class ApiService {
  constructor() {
    this.config = null;
    this.api = null;
  }

  /**
   * Update the service configuration
   * @param {Object} newConfig - New configuration from Parameter Store
   */
  updateConfig(newConfig) {
    this.config = newConfig;
    console.log('🔧 API Service configuration updated:', {
      endpoint: newConfig?.api?.endpoint ? 'configured' : 'default',
      bedrock: newConfig?.bedrock?.agentId ? 'configured' : 'default',
      features: Object.keys(newConfig?.features || {}).length
    });
    
    // Reset API instance to force recreation with new config
    this.api = null;
  }

  /**
   * Get or create configured API instance
   */
  async getConfiguredApi() {
    if (!this.api) {
      this.api = await this.createAxiosInstance();
    }
    return this.api;
  }

  /**
   * Create axios instance with current configuration
   */
  async createAxiosInstance() {
    const apiConfig = this.config || await initializeApiConfig();
    
    const instance = axios.create({
      baseURL: apiConfig?.api?.endpoint || 'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod',
      timeout: apiConfig?.api?.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...(apiConfig?.bedrock?.agentId && { 'X-Bedrock-Agent-Id': apiConfig.bedrock.agentId }),
        ...(apiConfig?.bedrock?.agentAlias && { 'X-Bedrock-Agent-Alias': apiConfig.bedrock.agentAlias }),
        ...(apiConfig?.bedrock?.modelId && { 'X-Bedrock-Model-Id': apiConfig.bedrock.modelId }),
        ...(apiConfig?.bedrock?.maxTokens && { 'X-Bedrock-Max-Tokens': apiConfig.bedrock.maxTokens }),
        ...(apiConfig?.bedrock?.temperature && { 'X-Bedrock-Temperature': apiConfig.bedrock.temperature })
      }
    });

    // Request interceptor for authentication
    instance.interceptors.request.use(
      async (config) => {
        try {
          const user = await getCurrentUser();
          if (user) {
            const session = await fetchAuthSession();
            // Use ID token for API Gateway Cognito User Pool authorizer (consistent with apiClient)
            if (session?.tokens?.idToken) {
              config.headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
            }
          }
          return config;
        } catch (error) {
          console.error('❌ [ConfiguredAPI] Error in request interceptor:', error);
          return Promise.reject(error);
        }
      },
      (error) => {
        console.error('❌ [ConfiguredAPI] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling with token refresh
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Phase 999.107 commit 7 + hotfix: same pattern as apiClient above —
        // trigger redirect but bubble the rejection so callers' finally{} fires.
        handleConsentRequired(error);
        if (error.response?.status === 403 && error.response?.data?.error === 'consent_required') {
          return Promise.reject(error);
        }

        // Handle 401 errors with token refresh (consistent with apiClient)
        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('🔄 Token expired in configured instance, attempting refresh...');
          originalRequest._retry = true;
          
          try {
            // Try to refresh the session
            const session = await fetchAuthSession({ forceRefresh: true });
            const newToken = session?.tokens?.idToken?.toString();
            
            if (newToken) {
              console.log('✅ Token refreshed successfully in configured instance');
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              delete originalRequest._retry;
              return instance(originalRequest);
            } else {
              throw new Error('No ID token in refreshed session');
            }
          } catch (refreshError) {
            console.error('❌ Token refresh failed in configured instance:', refreshError);
            // Clear auth state and let the error propagate
            try {
              window.localStorage.removeItem('amplify-signin-with-hostedUI');
              window.sessionStorage.clear();
            } catch (e) {
              console.warn('Could not clear storage:', e);
            }
          }
        }
        
        const errorMessage = handleApiError(error);
        console.error('API Error:', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }
    );

    return instance;
  }

  /**
   * Health Check with circuit breaker - Uses /chat endpoint instead of missing /health
   */
  async healthCheck() {
    // Check if circuit breaker is active
    if (window.circuitBreakerActive) {
      throw new Error('Health check skipped: Circuit breaker active');
    }
    
    try {
      // Use /sessions endpoint for health check as it's more reliable than /health
      const response = await apiClient.get('/sessions', { 
        timeout: 10000 
      });
      
      // Reset server error count on successful health check
      if (window.serverErrorCount) {
        window.serverErrorCount = 0;
      }
      return { status: 'healthy', endpoint: '/sessions' };
    } catch (error) {
      // Don't throw CORS errors to prevent infinite retries
      if (error.message.includes('CORS') || error.response?.status === 0) {
        console.log('🚫 CORS error detected, skipping health check retries');
        throw new Error('Health check failed: API endpoint not accessible');
      }
      
      // If sessions endpoint fails, try a simple OPTIONS request
      try {
        await apiClient.options('/', { timeout: 5000 });
        console.log('⚠️ Sessions endpoint down but API Gateway responding');
        return { status: 'degraded', endpoint: 'api-gateway-only' };
      } catch (optionsError) {
        throw new Error(`Health check failed: ${error.message}`);
      }
    }
  }

  /**
   * Make authenticated HTTP request with retry logic
   */
  async makeRequest(method, endpoint, data = null, options = {}) {
    const api = await this.getConfiguredApi();
    const config = {
      method,
      url: endpoint,
      timeout: options.timeout || 30000,
      ...options
    };

    if (data) {
      config.data = data;
    }

    return await api(config);
  }

  /**
   * Handle Bedrock Agent requests with progressive timeout strategy
   * Implements 20s → 35s → async fallback approach
   */
  async handleBedrockAgentRequest(api, payload, retryCount = 0, onProgressUpdate = null) {
    const timeouts = [20000, 35000]; // 20s, then 35s
    const currentTimeout = timeouts[retryCount] || 35000;
    
    try {
      // Create timeout promise with progress updates
      const timeoutPromise = new Promise((_, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('API_GATEWAY_TIMEOUT'));
        }, currentTimeout);
        
        // Progress updates every 5 seconds
        if (onProgressUpdate && currentTimeout > 15000) {
          const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            if (elapsed < currentTimeout - 1000) { // Stop 1s before timeout
              const phase = Math.floor(elapsed / 7000); // Change message every 7s
              const messages = [
                "AI Assistant is analyzing your question...",
                "Processing complex veteran support information...",
                "Consulting comprehensive DVA knowledge base...",
                "Preparing detailed response for your situation...",
                "Finalizing personalized guidance..."
              ];
              onProgressUpdate(messages[Math.min(phase, messages.length - 1)]);
            }
          }, 5000);
          
          // Clear interval when timeout occurs
          setTimeout(() => clearInterval(progressInterval), currentTimeout);
        }
      });
      
      const startTime = Date.now();
      
             // Make the API request
       const response = await Promise.race([
         api.post('/chat', payload, {
           timeout: currentTimeout + 5000 // Axios timeout slightly higher
         }),
         timeoutPromise
       ]);
      
      return response;
      
    } catch (error) {
      if (error.message === 'API_GATEWAY_TIMEOUT' || error.response?.status === 504) {
        if (retryCount < 1) {
          const nextTimeout = timeouts[retryCount + 1] || 35000;
          console.log(`⏳ Timeout occurred (${currentTimeout/1000}s), trying extended timeout (${nextTimeout/1000}s) - attempt ${retryCount + 1}...`);
          
          // Progressive delay between retries
          const delay = retryCount === 0 ? 2000 : 4000;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          return this.handleBedrockAgentRequest(api, payload, retryCount + 1, onProgressUpdate);
        } else {
          // After 2 attempts, suggest async processing
          throw new Error('BEDROCK_TIMEOUT_EXCEEDED');
        }
      }
      
      throw error;
    }
  }

  /**
   * Optimize message structure for better AI processing performance
   */
  optimizeMessageForProcessing(message) {
    // Clean up formatting and structure for better AI parsing
    let optimized = message
      .replace(/\n+/g, ' ') // Replace multiple newlines with spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    
    // If message contains numbered lists, preserve structure but optimize
    if (/^\d+\./.test(optimized)) {
      optimized = optimized.replace(/(\d+\.\s*)/g, '\n$1');
    }
    
    // Add context hint for better AI understanding and faster processing
    if (optimized.length > 100) {
      optimized = `Please provide concise guidance on: ${optimized}`;
    } else if (optimized.length > 50) {
      optimized = `Help with: ${optimized}`;
    }
    
    // Limit message length to prevent excessive processing time
    if (optimized.length > 2000) {
      optimized = optimized.substring(0, 1950) + "... [Please ask about specific aspects for detailed guidance]";
    }
    
    return optimized;
  }

  /**
   * Send chat message to Bedrock Agent with enhanced timeout and UX
   */
  async sendChatMessage(message, sessionId = null, retryCount = 0, onProgressUpdate = null) {
    const maxRetries = 2;
    
    try {
      // Import hybrid API service for chat operations
      const hybridApiService = (await import('./hybridApiService.js')).default;
      
      // Optimize message for faster processing
      const optimizedMessage = this.optimizeMessageForProcessing(message);
      
      console.log(`🤖 Sending chat message via enhanced API (attempt ${retryCount + 1}):`, {
        originalLength: message.length,
        optimizedLength: optimizedMessage.length,
        sessionId: sessionId?.substring(0, 8) + '...',
        retryCount
      });

      // Use hybrid API service for chat (routes to enhanced bedrock proxy)
      const response = await hybridApiService.sendChatMessage(optimizedMessage, sessionId);
      
      console.log('✅ Enhanced API response received:', {
        hasMessage: !!response?.response,
        messageLength: response?.response?.length || 0,
        sessionId: response?.sessionId
      });
      
      // Reset any circuit breaker state on success
      if (window.circuitBreakerState) {
        window.circuitBreakerState.failures = 0;
      }
      
      return response;
      
    } catch (error) {
      console.error(`❌ Chat API error (attempt ${retryCount + 1}):`, error.message);
      
      // Log CORS errors for development troubleshooting
      if (isDevelopment && error.message.includes('Network Error')) {
        console.log('🔧 [LOCALHOST] CORS Error - Backend not configured for localhost access');
        console.log('🔧 [LOCALHOST] To fix: Configure API Gateway CORS to allow http://localhost:3000');
      }
      
      // Handle specific error types with smart retry logic
      if (error.message.includes('502') || error.message.includes('503') || error.message.includes('504')) {
        // Gateway/Service errors - likely Lambda cold start
        if (retryCount < maxRetries) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 8000); // Exponential backoff, max 8s
          console.log(`⏳ Retrying in ${delay}ms... (Gateway/Service error)`);
          if (onProgressUpdate) {
            onProgressUpdate(`Retrying due to service delay... (${Math.ceil(delay/1000)}s)`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.sendChatMessage(message, sessionId, retryCount + 1, onProgressUpdate);
        }
      } else if (error.message.includes('429')) {
        // Rate limiting
        if (retryCount < maxRetries) {
          const delay = Math.min(3000 * Math.pow(2, retryCount), 12000); // Longer delay for rate limits
          console.log(`⏳ Rate limited, retrying in ${delay}ms...`);
          if (onProgressUpdate) {
            onProgressUpdate(`Rate limited, waiting ${Math.ceil(delay/1000)} seconds...`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.sendChatMessage(message, sessionId, retryCount + 1, onProgressUpdate);
        }
      } else if (error.message.includes('500') && retryCount < maxRetries) {
        // Other server errors
        const delay = Math.min(2500 * Math.pow(2, retryCount), 10000);
        console.log(`⏳ Server error, retrying in ${delay}ms...`);
        if (onProgressUpdate) {
          onProgressUpdate(`Server busy, retrying in ${Math.ceil(delay/1000)} seconds...`);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.sendChatMessage(message, sessionId, retryCount + 1, onProgressUpdate);
      }
      
      // Enhanced error messages for better user guidance
      let errorMessage = 'Failed to get AI response';
      if (error.message === 'BEDROCK_TIMEOUT_EXCEEDED') {
        errorMessage = `🤖 Your question requires extensive analysis and exceeded our processing time limit.

💡 **Try these approaches for faster responses:**
• **Break complex questions into parts** - Ask about one topic at a time
• **Be more specific** - Instead of "Tell me about benefits" try "What disability benefits am I eligible for?"
• **Use simpler language** - Avoid technical jargon when possible
• **Shorter questions** - Aim for 1-2 sentences when possible

**Examples of better questions:**
❌ "Tell me everything about DVA benefits, claims, healthcare, and education support for veterans with PTSD and physical disabilities"
✅ "What DVA benefits are available for PTSD?" (then ask follow-up questions)

**Still having issues?** Try refreshing the page or contact support.`;
      } else if (error.message.includes('Network Error')) {
        errorMessage = '🌐 Network connection issue. Please check your internet and try again.';
      } else if (error.message.includes('401') || error.message.includes('403')) {
        errorMessage = '🔐 Authentication error. Please refresh the page and try again.';
      } else if (error.message.includes('429')) {
        errorMessage = '⏳ Too many requests. Please wait a moment and try again.';
      } else if (error.message.includes('500')) {
        errorMessage = '🔧 Server error. Please try again in a moment.';
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Get user profile from the backend
   */
  async getUserProfile() {
    try {
      const api = await this.getConfiguredApi();
      const response = await api.get('/user');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get user profile:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // Properly format error message to avoid [object Object]
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(`Failed to get user profile: ${errorMessage}`);
    }
  }

  /**
   * Update user profile data
   */
  async updateUserProfile(profileData) {
    try {
      const api = await this.getConfiguredApi();
      const response = await api.put('/user', profileData);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update user profile:', error);
      // Properly format error message to avoid [object Object]
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Unknown error occurred';
      throw new Error(`Failed to update user profile: ${errorMessage}`);
    }
  }

  /**
   * Get user's chat sessions with retry logic and CORS fallback
   */
  async getChatSessions(retryCount = 0) {
    try {
      const api = await this.getConfiguredApi();
      const response = await api.get('/sessions');
      return response.data;
    } catch (error) {
      // Log CORS errors for development troubleshooting
      if (isDevelopment && error.message.includes('Network Error')) {
        console.log('🔧 [LOCALHOST] CORS blocked sessions - configure API Gateway CORS settings');
      }
      
      if (error.response?.status >= 500 && retryCount < 2) {
        console.log(`⏳ Retrying sessions fetch... (attempt ${retryCount + 2})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return this.getChatSessions(retryCount + 1);
      }
      throw new Error(`Failed to get chat sessions: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Generate military date-time format for chat titles: DDHHMMMMYY
   * Uses centralized utility to ensure consistency
   * Example: Chat 261550JUL25
   */
  generateChatTitle(existingSessions = []) {
    return generateMilitaryTitle(new Date(), existingSessions);
  }

  /**
   * Check if user has existing sessions from current login session
   */
  async getSessionsFromCurrentLogin() {
    try {
      const allSessions = await this.getChatSessions();
      const sessionList = allSessions.sessions || allSessions || [];
      
      // Filter out sessions without valid dates but keep all valid sessions
      const validSessions = sessionList.filter(session => {
        const dateValue = session.createdAt || session.timestamp;
        if (!dateValue) return false;
        
        const sessionDate = new Date(dateValue);
        if (isNaN(sessionDate.getTime())) return false;
        
        return true;
      });
      
      // Sort by creation time (most recent first)
      return validSessions.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.timestamp || 0);
        const dateB = new Date(b.createdAt || b.timestamp || 0);
        
        // Handle invalid dates by putting them at the end
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Failed to get sessions from current login:', error);
      return [];
    }
  }

  /**
   * Create new chat session with military date-time naming
   */
  async createChatSession(forceNew = false) {
    try {
      // Check for existing sessions from current login session
      if (!forceNew) {
        const currentLoginSessions = await this.getSessionsFromCurrentLogin();
        
        if (currentLoginSessions.length > 0) {
          return { session: currentLoginSessions[0] };
        }
      }
      
      // Get existing sessions to avoid title conflicts
      const existingSessions = await this.getSessionsFromCurrentLogin();
      
      // Generate new title with military date-time format
      const title = this.generateChatTitle(existingSessions);
      
      const response = await apiClient.post('/sessions', { title });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create chat session: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Get session details
   */
  async getSessionDetails(sessionId) {
    // Validate sessionId before making request
    if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
      throw new Error('Invalid session ID provided');
    }
    
    try {
      // Add explicit timeout for session details to prevent hanging on 500 errors
      const response = await Promise.race([
        apiClient.get(`/sessions/${sessionId}`),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session details timeout after 8 seconds')), 8000)
        )
      ]);
      
      return response.data;
    } catch (error) {
      // Log CORS errors for development troubleshooting
      if (isDevelopment && (error.message.includes('Network Error') || error.message.includes('CORS'))) {
        console.log('⚠️ Session details CORS error - configure API Gateway CORS settings');
      }
      
      // Enhanced error logging for 500 errors
      if (error.response?.status === 500) {
        console.error('🚨 Session details 500 error - backend may have stale session references');
        console.error('📋 Session ID:', sessionId);
      }
      
      throw new Error(`Failed to get session details: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Update session
   */
  async updateSession(sessionId, updates) {
    try {
      const response = await apiClient.put(`/sessions/${sessionId}`, updates);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update session: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Delete session
   */
  async deleteSession(sessionId) {
    try {
      const response = await apiClient.delete(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to delete session: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Create or update a session's metadata (Phase 999.102 — cloud-only sessions).
   * Idempotent on sessionId — same id is upserted, not duplicated.
   *
   * @param {{ sessionId: string, title?: string, source?: 'chat' | 'advocate' }} payload
   * @returns {Promise<{ sessionId: string, title: string, source: string, createdAt: string, updatedAt: string }>}
   */
  async createOrUpdateSession(payload) {
    if (!payload || !payload.sessionId) {
      throw new Error('createOrUpdateSession: sessionId required');
    }
    try {
      const response = await apiClient.post('/sessions', payload);
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to upsert session: ${error.response?.data?.error || error.message}`,
      );
    }
  }

  /**
   * Append messages to a session (Phase 999.102 — cloud-only sessions).
   *
   * @param {string} sessionId
   * @param {Array<{ role: 'user' | 'assistant', content: string, timestamp?: string, messageId?: string }>} messages
   * @returns {Promise<{ appendedCount: number, messageIds: string[] }>}
   */
  async appendSessionMessages(sessionId, messages) {
    if (!sessionId) throw new Error('appendSessionMessages: sessionId required');
    if (!Array.isArray(messages) || messages.length === 0) {
      return { appendedCount: 0, messageIds: [] };
    }
    try {
      const response = await apiClient.post(
        `/sessions/${sessionId}/messages`,
        { messages },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to append messages: ${error.response?.data?.error || error.message}`,
      );
    }
  }

  /**
   * List sessions filtered by source (Phase 999.102).
   *
   * @param {{ source?: 'chat' | 'advocate' }} [options]
   * @returns {Promise<Array<{ sessionId: string, title: string, source: string, lastMessageAt: string, messageCount: number }>>}
   */
  async listSessionsBySource(options = {}) {
    try {
      const params = options.source ? { source: options.source } : undefined;
      const response = await apiClient.get('/sessions', { params });
      return response.data?.sessions || [];
    } catch (error) {
      throw new Error(
        `Failed to list sessions: ${error.response?.data?.error || error.message}`,
      );
    }
  }

  /**
   * Get current user information including username and email
   */
  async getCurrentUserInfo() {
    try {
      console.log('🔍 Getting current user info...');
      
      // Get user from Cognito
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      
      // Extract user info from tokens
      const userInfo = {
        username: user.username,
        email: user.signInDetails?.loginId || 'N/A',
        tokens: {
          accessToken: session.tokens?.accessToken?.toString(),
          idToken: session.tokens?.idToken?.toString()
        },
        isAuthenticated: !!(session.tokens?.accessToken && session.tokens?.idToken)
      };
      
      console.log('✅ Assembled user info:', { 
        username: userInfo.username, 
        email: userInfo.email,
        hasTokens: !!(userInfo.tokens.accessToken && userInfo.tokens.idToken)
      });
      
      return userInfo;
    } catch (error) {
      console.error('❌ getCurrentUserInfo error:', error);
      throw new Error(`Failed to get current user info: ${error.message}`);
    }
  }

  /**
   * Warms up the WebSocket Lambda specifically
   * @returns {Promise<void>}
   */
  async warmUpWebSocketLambda() {
    // Circuit breaker: prevent too frequent warmup attempts
    const now = Date.now();
    if (this.lastWarmupAttempt && (now - this.lastWarmupAttempt) < 10000) {
      console.log('🔄 WebSocket warmup skipped - too recent (circuit breaker)');
      return { status: 'skipped', reason: 'circuit_breaker' };
    }
    
    this.lastWarmupAttempt = now;
    console.log('🔥 Attempting WebSocket Lambda warmup...');
    
    // Simple timeout-based approach to avoid infinite loops
    return new Promise((resolve) => {
      const startTime = Date.now();
      const websocketConfig = getWebSocketConfig();
      const websocketUrl = websocketConfig.url;
      
      // Get auth token for WebSocket connection
      const getAuthToken = async () => {
        try {
          const user = await getCurrentUser();
          if (!user) {
            throw new Error('No authenticated user for WebSocket warmup');
          }
          const session = await fetchAuthSession();
          return session?.tokens?.accessToken?.toString();
        } catch (error) {
          console.warn('⚠️ Could not get auth token for WebSocket warmup, trying without auth');
          return null;
        }
      };
      
      const performWarmup = async () => {
        try {
          const authToken = await getAuthToken();
          const wsUrl = authToken ? `${websocketUrl}?token=${authToken}` : websocketUrl;
          
          console.log('🔌 Creating temporary WebSocket connection for Lambda warmup...');
          const tempWs = new WebSocket(wsUrl);
          
          const cleanup = () => {
            try {
              if (tempWs.readyState === WebSocket.OPEN || tempWs.readyState === WebSocket.CONNECTING) {
                tempWs.close(1000, 'Warmup complete');
              }
            } catch (error) {
              // Ignore cleanup errors
            }
          };
          
          // Always resolve after 5 seconds maximum
          const maxWarmupTime = setTimeout(() => {
            cleanup();
            const duration = Date.now() - startTime;
            console.log(`✅ WebSocket Lambda warmup completed after ${duration}ms`);
            resolve({ status: 'timeout', duration });
          }, 5000); // 5 second maximum
          
          tempWs.onopen = () => {
            console.log('🔗 WebSocket warmup connection established');
            
            // Send warmup ping
            try {
              tempWs.send(JSON.stringify({
                action: 'warmup',
                sessionId: 'warmup-session',
                timestamp: new Date().toISOString(),
                purpose: 'lambda_preparation'
              }));
              console.log('📤 Warmup ping sent to WebSocket Lambda');
            } catch (error) {
              console.warn('⚠️ Failed to send warmup ping (non-critical):', error.message);
            }
            
            // Close connection after brief delay to ensure Lambda is warm
            setTimeout(() => {
              cleanup();
              clearTimeout(maxWarmupTime);
              const duration = Date.now() - startTime;
              console.log(`✅ WebSocket Lambda warmup completed in ${duration}ms`);
              resolve({ status: 'success', duration });
            }, 1500); // 1.5 second connection
          };
          
          tempWs.onerror = (error) => {
            cleanup();
            clearTimeout(maxWarmupTime);
            const duration = Date.now() - startTime;
            console.warn(`⚠️ WebSocket warmup failed after ${duration}ms (non-critical):`, error);
            resolve({ status: 'failed', duration, error: error.message });
          };
          
          tempWs.onclose = (event) => {
            if (event.code !== 1000) {
              console.log('🔄 WebSocket warmup connection closed:', event.code, event.reason);
            }
          };
          
          tempWs.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              console.log('📥 WebSocket warmup response:', data.type || data.action || 'unknown');
              
              // If we get any response, the Lambda is definitely warm
              if (data.type === 'pong' || data.action === 'warmup_confirmed') {
                cleanup();
                clearTimeout(maxWarmupTime);
                const duration = Date.now() - startTime;
                console.log(`✅ WebSocket Lambda confirmed warm in ${duration}ms`);
                resolve({ status: 'confirmed', duration });
              }
            } catch (error) {
              console.warn('⚠️ WebSocket warmup response parse error (non-critical):', error.message);
            }
          };
          
        } catch (error) {
          const duration = Date.now() - startTime;
          console.warn(`⚠️ WebSocket warmup failed after ${duration}ms (non-critical):`, error.message);
          resolve({ status: 'failed', duration, error: error.message });
        }
      };
      
      performWarmup();
    });
  }

  /**
   * Warms up the WebSocket Lambda to prevent cold starts
   * @returns {Promise<void>}
   */
  async warmUpBackend() {
    try {
      console.log(`🔥 Warming up WebSocket Lambda for immediate veteran support...${isDevelopment ? ' [FROM LOCALHOST]' : ''}`);
      
      const startTime = Date.now();
      
      // Warm up critical endpoints in parallel with overall timeout
      const warmupPromises = [
        // 1. Warm up WebSocket Lambda (most critical - this is where chat actually happens)
        this.warmUpWebSocketLambda().catch(error => {
          if (isDevelopment && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
            console.log('⚠️ WebSocket warm-up CORS error (expected in localhost development)');
          } else {
            console.log('⚠️ WebSocket warm-up failed (non-critical):', error.message);
          }
          return null;
        }),
        
        // 2. Warm up sessions endpoint
        this.makeRequest('GET', '/sessions', null, { 
          timeout: 30000
        }).catch(error => {
          if (isDevelopment && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
            console.log('⚠️ Sessions warm-up CORS error (expected in localhost development)');
          } else {
            console.log('⚠️ Sessions warm-up failed (non-critical):', error.message);
          }
          return null;
        }),

        // 3. Test user endpoint (lightweight health check alternative) - DISABLED
        (() => {
          // User endpoint disabled during warmup due to intermittent 500 errors
          // This is non-critical as main chat functionality works perfectly
          console.log('⏭️ User endpoint warmup skipped (non-critical service)');
          return Promise.resolve(null);
        })()
      ];

      // Execute all warm-up requests with timeout
      const warmupResult = await Promise.race([
        Promise.allSettled(warmupPromises),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Backend warmup timeout after 8 seconds')), 8000)
        )
      ]);
      
      // Check if any critical services are working
      const results = Array.isArray(warmupResult) ? warmupResult : [];
      const sessionsWorking = results[1]?.status === 'fulfilled';
      
      if (!sessionsWorking) {
        console.log('⚠️ Sessions service may be experiencing issues during warmup');
      }
      
      const duration = Date.now() - startTime;
      console.log(`✅ Backend warm-up completed in ${duration}ms - ready for instant veteran support!${isDevelopment ? ' [LOCALHOST READY]' : ''}`);
      
      // Development mode - additional connection verification
      if (isDevelopment) {
        console.log('🔍 [LOCALHOST] Connection verified to AWS backend from development environment');
        console.log('🔍 [LOCALHOST] API Base URL:', API_BASE_URL);
        console.log('🔍 [LOCALHOST] Authentication working with Cognito User Pool');
        console.log('🔍 [LOCALHOST] Note: Some CORS errors are expected when testing from localhost');
      }
      
    } catch (error) {
      console.warn('⚠️ Backend warm-up had issues (non-critical):', error.message);
      // Non-blocking - app continues to work even if warm-up fails
    }
  }

  /**
   * Send a test message to verify AI agent responsiveness during warmup
   * @returns {Promise<Object>} Response from AI agent
   */
  async sendWarmupTestMessage() {
    try {
      console.log('🤖 Sending AI agent readiness test...');
      
      const payload = {
        message: "System test - please confirm AI agent is ready to assist veterans",
        sessionId: "warmup-test-session",
        userId: "system-warmup", 
        isWarmupTest: true,
        requireResponse: true,
        testId: generateRequestId()
      };

      const response = await Promise.race([
        apiClient.post('/chat', payload, {
          timeout: 65000 // 65 seconds timeout for warmup test
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Warmup test timeout after 65 seconds')), 65000)
        )
      ]);
      
      if (response.data && (response.data.message || (response.data.aiMessage && response.data.aiMessage.content))) {
        console.log('✅ AI agent test successful - received response');
        return response.data;
      } else {
        throw new Error('AI agent returned empty response');
      }
      
    } catch (error) {
      console.error('❌ AI agent readiness test failed:', error.response?.data || error.message);
      
      // Log CORS errors for development troubleshooting
      if (isDevelopment && (error.message.includes('Network Error') || error.message.includes('CORS'))) {
        console.log('🔧 [LOCALHOST] CORS blocked - configure API Gateway CORS to allow localhost');
      }
      
      // Enhanced error messages for warmup test failures
      let errorMessage = 'AI agent readiness test failed';
      if (error.message.includes('timeout')) {
        errorMessage += ': Request timeout (AI service may be warming up)';
      } else if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
        errorMessage += ': Service unavailable (likely Lambda cold start)';
      } else if (error.response?.status === 429) {
        errorMessage += ': Rate limited';
      } else if (error.response?.status === 401) {
        errorMessage += ': Authentication error';
      } else if (error.response?.data?.error) {
        errorMessage += `: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Map frontend feedback types to backend-accepted values
   * Backend accepts: 'chat', 'general', 'bug', 'feature', 'support'
   */
  mapFeedbackType(frontendType) {
    const typeMapping = {
      'quick_rating': 'chat',        // Quick thumbs up/down on chat responses
      'detailed': 'general',         // Detailed feedback with comments
      'session_rating': 'general',   // End-of-session ratings
      'bug_report': 'bug',          // Bug reports and issues
      'feature_request': 'feature',  // Feature requests
      'support_request': 'support'   // Support requests
    };
    
    const mappedType = typeMapping[frontendType] || 'general';
    console.log(`🔄 Mapping feedback type: ${frontendType} → ${mappedType}`);
    return mappedType;
  }

  /**
   * Submit user feedback for a message or session
   */
  async submitFeedback(feedbackData) {
    try {
      console.log('📝 Submitting feedback:', feedbackData);
      
      // Map frontend feedback type to backend-accepted value
      const mappedType = this.mapFeedbackType(feedbackData.feedbackType);
      
      const response = await apiClient.post('/feedback', {
        sessionId: feedbackData.sessionId,
        messageId: feedbackData.messageId,
        type: mappedType, // Use mapped type that backend accepts
        rating: feedbackData.rating,
        feedbackText: feedbackData.feedbackText || '',
        category: feedbackData.category || 'general',
        platform: 'web',
        timestamp: new Date().toISOString()
      });

      console.log('✅ Feedback submitted successfully');
      return response.data;
      
    } catch (error) {
      console.error('❌ Failed to submit feedback:', error);
      throw new Error(`Failed to submit feedback: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Submit open-ended product feedback from the Beta modal — Phase 999.108.
   *
   * Posts to /product-feedback (afterservice-product-feedback-v1 Lambda).
   * The Lambda extracts the user's identity (firstName, lastName, email)
   * server-side from the Cognito JWT + DDB profile, so the client only
   * needs to send the feedback text. Result is delivered via SES to
   * hello@afterservice.ai with Reply-To set to the user's email.
   *
   * Distinct from `submitFeedback` above which handles per-message
   * thumbs ratings inside the chat.
   *
   * @param {string} message - User's feedback text (1-5000 chars).
   * @returns {Promise<{status:string, messageId:string}>}
   * @throws Re-throws the axios error so the caller can map
   *         err.response.data.error -> user-facing copy.
   */
  async submitProductFeedback(message) {
    const response = await apiClient.post('/product-feedback', { message });
    return response.data;
  }

  /**
   * Get user's feedback history
   */
  async getUserFeedback(limit = 50) {
    try {
      console.log('📊 Getting user feedback history...');
      
      const response = await apiClient.get(`/feedback?limit=${limit}`);

      console.log(`✅ Retrieved ${response.data.count} feedback items`);
      return response.data;
      
    } catch (error) {
      console.error('❌ Failed to get feedback history:', error);
      throw new Error(`Failed to retrieve feedback: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Submit session rating (for end-of-session feedback)
   */
  async submitSessionRating(sessionId, rating, comments = '') {
    try {
      console.log('⭐ Submitting session rating:', { sessionId, rating });
      
      return await this.submitFeedback({
        sessionId,
        feedbackType: 'session_rating',
        rating,
        feedbackText: comments,
        category: 'session_quality'
      });
      
    } catch (error) {
      console.error('❌ Failed to submit session rating:', error);
      throw new Error(`Failed to submit session rating: ${error.message}`);
    }
  }

  /**
   * Report a bug or issue
   */
  async reportBug(sessionId, messageId, description, severity = 'medium') {
    try {
      console.log('🐛 Reporting bug:', { sessionId, messageId, severity });
      
      return await this.submitFeedback({
        sessionId,
        messageId,
        feedbackType: 'bug_report',
        rating: 1, // Bugs are negative feedback
        feedbackText: description,
        category: 'bug',
        metadata: {
          severity,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });
      
    } catch (error) {
      console.error('❌ Failed to report bug:', error);
      throw new Error(`Failed to report bug: ${error.message}`);
    }
  }

  /**
   * Upload file (if needed for future features)
   */
  async uploadFile(file, sessionId) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sessionId', sessionId);

      const response = await apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      throw new Error(`Failed to upload file: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Development utility - Test localhost connection to AWS backend
   */
  async testLocalhostConnection() {
    if (!isDevelopment) {
      console.warn('⚠️ testLocalhostConnection() should only be used in development');
      return;
    }

    console.log('🧪 [LOCALHOST] Testing connection to AWS backend...');
    
    try {
      // Test basic connectivity
      const healthResponse = await this.healthCheck();
      console.log('✅ [LOCALHOST] Health check successful:', healthResponse);
      
      // Test authentication
      const userInfo = await this.getCurrentUserInfo();
      console.log('✅ [LOCALHOST] Authentication successful:', userInfo.username);
      
      // Test session retrieval
      const sessions = await this.getChatSessions();
      console.log('✅ [LOCALHOST] Session retrieval successful:', sessions.length, 'sessions');
      
      console.log('🎉 [LOCALHOST] All connection tests passed! Ready for agent interaction.');
      return true;
      
    } catch (error) {
      console.error('❌ [LOCALHOST] Connection test failed:', error.message);
      console.error('🔧 [LOCALHOST] Troubleshooting tips:');
      console.error('   1. Check AWS Cognito User Pool callback URLs include localhost');
      console.error('   2. Verify API Gateway CORS settings allow localhost origins'); 
      console.error('   3. Ensure you are logged in with valid credentials');
      return false;
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;// Export individual methods for convenience - properly bound to maintain context
export const healthCheck = apiService.healthCheck.bind(apiService);
export const sendChatMessage = apiService.sendChatMessage.bind(apiService);
export const getUserProfile = apiService.getUserProfile.bind(apiService);
export const updateUserProfile = apiService.updateUserProfile.bind(apiService);
export const getChatSessions = apiService.getChatSessions.bind(apiService);
export const createChatSession = apiService.createChatSession.bind(apiService);
export const getSessionDetails = apiService.getSessionDetails.bind(apiService);
export const updateSession = apiService.updateSession.bind(apiService);
export const deleteSession = apiService.deleteSession.bind(apiService);
export const getCurrentUserInfo = apiService.getCurrentUserInfo.bind(apiService);
export const uploadFile = apiService.uploadFile.bind(apiService);
export const testLocalhostConnection = apiService.testLocalhostConnection.bind(apiService);
export { validateChatMessage };

